import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography, Button } from "@mui/material";
import "../styles/MonerisIframe.css";
import { appInsights } from "../components/AppInsights";
import apiCall from "../APICalls/APIcalls";

const MonerisIframe = () => {
  const [checkoutToken, setCheckoutToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState(null); 
  const [error, setError] = useState("");
  const [showCheckout, setShowCheckout] = useState(true);

  const monerisRef = useRef(null);


  useEffect(() => {
  const loadFromGuid = async () => {
    try {
      setLoading(true);
      setError("");

      //Fetching key UUID from URL
      const params = new URLSearchParams(window.location.search);
      let guid = params.get("SID");
      const sid = guid.replace("SID={", "").replace("}", "");
      if (!sid) {
        setError("Invalid payment link. GUID missing.");
        setLoading(false);
        return;
      }

      // Call backend with GUID to fetch payment token
      const response = await apiCall(`${sid}`);

      if (!response && response != "GUID not found") {
        throw new Error("Payment lookup failed");
      }

      let encryptedToken = response;
      
      if (!encryptedToken) {
        setError("There is no active gateway for this session.");
        setLoading(false);
        return;
      }

      const decryptData = encryptedToken;

      setCheckoutToken(decryptData);

      const cleanUrl = `${window.location.origin}/moneris/checkout`;
      window.history.replaceState({}, document.title, cleanUrl);

      // Clean URL
      window.history.replaceState({}, document.title, "/moneris/checkout");

    } catch (err) {
      appInsights.trackException({
        exception: err,
        properties: {
          component: "MonerisIframe",
          method: "loadFromGuid"
        }
      });

      setError(err.message || "Unable to load payment session.");
      setLoading(false);
    }
  };

  loadFromGuid();
}, []);

  // Load Moneris script & init checkout
  useEffect(() => {
    if (!checkoutToken) return;

    const loadMonerisScript = async () => {
      try {
        setError("");
        setLoading(true);
        // get congif API call
        const response = await fetch("/api/config");
        
        if (!response.ok) { 
          appInsights.trackException({ 
            exception: new Error("Config API failed"), 
            properties: { component: "MonerisIframe", 
            method: "loadMonerisScript", 
            status: response.status, 
          }, 
        }); 
        setError("Config API failed"); 
      }

        const config = await response.json();
        const MONERIS_SCRIPT_URL = "/api/moneris-script";
        const MONERIS_MODE = config.REACT_APP_MONERIS_MODE;

        if (!MONERIS_MODE) {
          appInsights.trackException({
            exception: new Error("Missing Moneris mode in Application Configuration"),
            properties: {
              component: "MonerisIframe",
              method: "loadMonerisScript",
            },
          });
          setError("Missing Moneris mode in Application Configuration.");
          setLoading(false);
          return;
        }
      
        // Moneris initialization
        const initMoneris = () => {
          try{
            const Moneris = window.monerisCheckout;
          if (!Moneris) {
            appInsights.trackException({ 
              exception: new Error("Moneris library not available"), 
              properties: { 
                component: "MonerisIframe", 
                method: "initMoneris", 
              }, 
            });
            setError("Moneris library failed to load.");
            setLoading(false);
            return;
          }

          monerisRef.current = new window.monerisCheckout();
          monerisRef.current.setMode(config.REACT_APP_MONERIS_MODE);
          monerisRef.current.setCheckoutDiv("monerisCheckout");

          //page loaded
          monerisRef.current.setCallback("page_loaded", () => {
            appInsights.trackEvent({
              name: "Moneris Page Loaded",
              properties: { component: "MonerisIframe" },
            });
            setLoading(false);
          });

          //Cancel Transaction
          monerisRef.current.setCallback("cancel_transaction", () => {
              monerisRef.current.closeCheckout();

            appInsights.trackEvent({ 
              name: "Moneris Payment Cancelled", 
              properties: { 
                component: "MonerisIframe" 
              }, 
            });
            setLoading(false);
            setShowCheckout(false);
            setMessage("Payment was cancelled.");
            setMessageType("cancel");
          });

          monerisRef.current.setCallback("payment_receipt", (data) => {
            // console.log("Payment Receipt", data);Applicaion insight
          });

          //transaction Successfull
          monerisRef.current.setCallback("payment_complete", (data) => {
            
            if (monerisRef.current?.closeCheckout) {
              monerisRef.current.closeCheckout();
            }
            appInsights.trackEvent({ 
              name: "Moneris Payment Success", 
              properties: { component: "MonerisIframe", 
                receipt: typeof data === "string" ? data : JSON.stringify(data), 
              }, 
            });

            setLoading(false);
            setShowCheckout(false);
            setMessage("Transaction completed successfully!");
            setMessageType("success");
          });

          // Trasaction Timeout
          monerisRef.current.setCallback("timeout", () => {

            if (monerisRef.current?.closeCheckout) {
              monerisRef.current.closeCheckout();
            }
            appInsights.trackEvent({ 
              name: "Moneris Payment Timeout", 
              properties: {
                 component: "MonerisIframe" 
                }, 
              });
            setLoading(false);
            setError(
              "Your payment session has timed out. Please restart the payment."
            );
            setShowCheckout(false);
          });

          monerisRef.current.startCheckout(checkoutToken);
        }catch (err) {
           setLoading(false); 
           appInsights.trackException({ 
            exception: err, 
            properties: { 
              component: "MonerisIframe", 
              method: "initMoneris-catch", 
            }, 
          }); 
          setError("Failed to initialize payment gateway.");
         } 
        };

        // Load Script
          const existingScript = Array.from(document.querySelectorAll("script")).find(
            (s) => s.getAttribute("src") === MONERIS_SCRIPT_URL
          );

        if (existingScript) {
          initMoneris();
          return;
        }

        const script = document.createElement("script");
        script.src = MONERIS_SCRIPT_URL;
        script.async = true;

        script.onload = initMoneris;
        script.onerror = () => {
          setLoading(false);
          appInsights.trackException({ 
            exception: new Error("Moneris script failed integrity verification. You may need to generate a new hash — the source content might have changed."), 
            properties: { 
              component: "MonerisIframe", 
              method: "scriptLoad", 
            }, 
          });
          setError(
            "Payment service unavailable. Please try again later."
          );
          
        };

        document.body.appendChild(script);
      } catch (err) {
        setLoading(false);
       appInsights.trackException({ 
        exception: err, 
        properties: { 
          component: "MonerisIframe", 
          method: "loadMonerisScript-catch", 
        }, 
      });
        setError("Could not load Moneris configuration.");
        
      }
    };

    loadMonerisScript();
  }, [checkoutToken]);

  const handleCloseWindow = () => {
    try {
      window.open("", "_self");
      window.close();
    } catch (e) {
      window.location.href = "/";
    }
  };

  return (
    <Box position="relative" minHeight="500px">
      {/* Loader Overlay */}
      {loading && !error && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255,255,255,0.8)"
          zIndex={10}
        >
          <CircularProgress />
          <Typography variant="h6" mt={2}>
            Please wait, your payment is getting processed...
          </Typography>
        </Box>
      )}

      {/* Error Overlay */}
      {!loading && error && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255,255,255,0.9)"
          zIndex={10}
        >
          <Typography color="error" variant="h6">
            {error}
          </Typography>
        </Box>
      )}

      {/* Success / Cancel Overlay */}
      {!loading && !error && message && (
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          bgcolor="rgba(255,255,255,0.95)"
          zIndex={10}
        >
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: messageType === "success" ? "green" : "red",
              fontWeight: 600,
            }}
          >
            {message}
          </Typography>

          <Typography variant="body1" mt={2}>
            You may now close this window
          </Typography>

          <Button
            variant="contained"
            color="success"
            sx={{ mt: 2 }}
            onClick={handleCloseWindow}
          >
            OK
          </Button>
        </Box>
      )}

      {/* Checkout Container */}
      {showCheckout && (
        <Box
          id="monerisCheckout"
          style={{
            minHeight: "400px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            overflow: "hidden",
          }}
        />
      )}
    </Box>
  );
};

export default MonerisIframe;